import styled from 'styled-components';
import Button from 'common/components/atoms/Button';
import { FlexContainer } from 'common/components/atoms';
import MultiSelectDropdown_T from 'common/components/molecules/MultiSelectDropdown';
import DropdownSelect_T from 'common/components/molecules/DropdownSelect';
import InputField from 'common/components/atoms/InputField';
import Label_T from 'common/typography/Label/Label';
import H5 from 'common/typography/H5/H5';
import colors from 'common/styles/colors';
import Text_T from 'common/typography/Text/Text';
import SecondaryText_T from 'common/typography/SecondaryText/SecondaryText';
import {
  getPartnerPrimaryColor,
  getPartnerHoverTintBg,
  getTemplateBannerTitleColor,
  getCostSavedHighlightStyles,
} from 'common/styles/organisationPartnerTheme';

const primaryColor = theme => getPartnerPrimaryColor(theme);

/** Labels / tab headings: partner theme defines primaryTextColor per brand. */
const sectionHeadingColor = (theme) => {
  if (theme && theme.primaryTextColor) return theme.primaryTextColor;
  return primaryColor(theme);
};

const tintBg = theme => getPartnerHoverTintBg(theme);
const pillHoverBg = () => '#EEF2FF';

/* Task modal right column: keep these attrs and rules when resolving merges (do not change spacing/padding here unless explicitly requested). */
export const DropdownSelect = styled(DropdownSelect_T).attrs({
  fontSize: '13px',
  minHeight: '32px'
})`
  .span-label {
    font-weight: 600;
    color: ${props => sectionHeadingColor(props.theme)};
  }

  /* Base component's border is on its immediate child container */
  &:hover > div {
    border-color: ${props => primaryColor(props.theme)};
  }
`;

export const H3 = styled.span`
  font-size: 12px;
  font-family: Petasense Open Sans;
  ${props => props.color && `color: ${props.color};`}
  cursor: pointer;
`;

export const SelectTemplateBanner = styled.div`
  border: 1px dashed #D1D5DB;
  background-color: #F9FAFB;
  border-radius: 10px;
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  margin-bottom: 16px;
  &:hover {
    border-color: ${props => primaryColor(props.theme)};
    background-color: ${props => tintBg(props.theme)};
  }
`;

export const SelectTemplateTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${props => getTemplateBannerTitleColor(props.theme)};
`;

export const SelectTemplateSubtitle = styled.span`
  margin-top: 4px;
  font-size: 12px;
  color: #6B7280;
`;

export const TemplateButton = styled(Button)`
  padding: 8px 16px;
  border-radius: 999px;
  border: none;
  background-color: ${props => (props.active ? primaryColor(props.theme) : '#FFFFFF')};
  color: ${props => (props.active ? '#FFFFFF' : primaryColor(props.theme))};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 0 0 1px ${props => (props.active ? primaryColor(props.theme) : '#E4E7EC')};
  white-space: nowrap;
  &:hover {
    background-color: ${props => primaryColor(props.theme)};
    color: #FFFFFF;
    box-shadow: 0 0 0 2px rgba(148, 163, 184, 0.25);
  }
`;

export const Title = styled(H5)`
  font-weight: bold;
  margin: 0 0 1em 0;
  line-height: 0;
`;

export const Description = styled(H5)`
  font-weight: 300;
  margin: 0 0 1.5em 0;
`;

const Label = styled(Label_T)`
  color: ${props => props.theme.colors.greyXXD};
  font-weight: 400;
  font-size: 20px;
  line-height: 27px;
  margin-bottom: 12px;
  display: block;
`;

export const RowContainer = styled(FlexContainer).attrs({ justifyContent: 'space-between' })`
  ${InputField} {
    flex-basis: 49%;
    ${Label} {
      color: ${props => props.theme.colors.greyXXD};
      ${props => props.theme.fonts.boldLabel};
      margin-bottom: 6px
    }
  }
`;

/* MultiSelectDropdown border is defined in global SCSS on the wrapper class */
export const MultiSelectDropdown = styled(MultiSelectDropdown_T)`
  &:hover {
    border-color: ${props => primaryColor(props.theme)};
  }
`;

export const SelectFieldWrapper = styled.div`
  .ui.selection.dropdown {
    &:hover {
      border-color: ${props => primaryColor(props.theme)} !important;
    }
  }
`;

export const EditIconContainer = styled.span`
  margin-left: 10px;
  cursor: pointer;
`;

export const SectionHeader = styled(Label_T)`
  color: ${props => sectionHeadingColor(props.theme)};
  font-weight: 600;
  font-size: 13px;
  letter-spacing: normal;
  text-transform: none;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: "Petasense Open Sans", sans-serif;
`;

export const SectionBlock = styled.div`
  margin-bottom: 8px;
  padding-bottom: 0;
`;

/* Right pane wrapper: only flattens SelectFieldWrapper inner margins; preserve on merge (no extra gap/padding added here). */
export const RightPaneFields = styled.div`
  ${SelectFieldWrapper} > div,
  ${SelectFieldWrapper} .field {
    margin-bottom: 0 !important;
  }
`;

// Shared styles for RepairsTimeline.js and NotesTimeline.js
export const TimelineNoDataContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 4rem;
  color: ${colors.greyD};
`;

export const TimelineItemContainer = styled.div`
  display: flex;
  padding: 1rem;
  position: relative;
  margin-bottom: 1rem;
  background-color: ${colors.greyXXXL};
  border-radius: 5px;
  border: 1px solid ${colors.white};
  cursor: pointer;
  &:hover {
    background-color: ${colors.greyXXL};
    border: 1px solid ${colors.greyD};
  }
`;

export const TimelineText = styled(Text_T)`
  font-size: 14px;
  word-break: break-word;
  color: ${props => props.color || colors.black};
`;

export const TimelineSecondaryText = styled(SecondaryText_T)`
  font-size: 12px;
  line-height: 160%;
  margin-top: 6px;
`;

export const TimelineIconContainer = styled(FlexContainer).attrs({
  justifyContent: 'center',
  alignItems: 'center'
})`
  padding-right: 1rem;
`;

export const ActivityTabRow = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid #E4E7EC;
  margin-bottom: 0;
  gap: 0;
`;

export const ActivityTab = styled.button`
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  font-family: "Petasense Open Sans", sans-serif;
  color: #6B7280;
  cursor: pointer;
  margin-bottom: -1px;
  &:hover {
    color: ${props => sectionHeadingColor(props.theme)};
  }
  ${props => props.active && `
    color: ${sectionHeadingColor(props.theme)};
    border-bottom-color: ${sectionHeadingColor(props.theme)};
  `}
`;

export const TemplateCardsRow = styled(FlexContainer)`
  flex-wrap: wrap;
  gap: 16px 16px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 4px;
  max-height: 32rem;

  /* two-column grid */
  ${'' /* ensure children wrap into two columns */};

  /* vertical scrollbar styling in WebKit */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #D1D5DB;
    border-radius: 3px;
  }
`;

export const TemplateFilterRow = styled(FlexContainer)`
  flex-wrap: wrap;
  gap: 8px;
`;

export const TemplateFilterPill = styled.button`
  border: none;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 11px;
  font-weight: 600;
  font-family: "Petasense Open Sans", sans-serif;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-transform: none;
  background-color: ${props => (props.active ? (primaryColor(props.theme)) : '#F3F4F6')};
  color: ${props => (props.active ? '#FFFFFF' : '#4B5563')};
  transition: background-color 0.15s ease, color 0.15s ease;
  &:hover {
    background-color: ${props => (
      props.active
        ? (primaryColor(props.theme))
        : pillHoverBg(props.theme)
    )};
  }
`;

/** Own row below pills so expand/collapse is visible on all themes (not lost in wrapped gray pills). */
export const TemplateFilterExpandRow = styled(FlexContainer).attrs({
  alignItems: 'center',
  justifyContent: 'flex-start',
  direction: 'row'
})`
  width: 100%;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 10px;
`;

export const TemplateFilterToggle = styled.button`
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-family: "Petasense Open Sans", sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  color: ${props => primaryColor(props.theme)};
  text-decoration: underline;
  text-underline-offset: 2px;
  &:hover {
    opacity: 0.85;
  }
  &:focus {
    outline: none;
  }
`;

export const BackToDetailsButton = styled(Button)`
  border-radius: 999px !important;
  border: 1px solid #E4E7EC !important;
  background-color: #FFFFFF !important;
  color: #111827 !important;
  padding: 8px 18px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  box-shadow: none !important;
  &:hover {
    border-color: ${props => primaryColor(props.theme)} !important;
    background-color: ${props => tintBg(props.theme)} !important;
    color: #111827 !important;
  }
`;

/** Cost-savings nested modal: match partner primary by hostname (same as SectionHeader). */
export const CloseTaskConfirmButton = styled(Button)`
  background-color: ${props => primaryColor(props.theme)} !important;
  border: 1px solid ${props => primaryColor(props.theme)} !important;
  color: ${props => props.theme.colors.white} !important;
  &:hover:not(:disabled) {
    border: 1px solid ${props => props.theme.colors.greyXXD} !important;
  }
`;

export const TemplateCard = styled.div`
  flex: 0 0 calc(50% - 8px); /* two cards per row with gap */
  max-width: calc(50% - 8px);
  padding: 14px 18px;
  border-radius: 8px;
  border: 1px solid #E5E7EB;
  background-color: #FFFFFF;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    border-color: ${props => primaryColor(props.theme)};
    box-shadow: 0 2px 6px rgba(15, 23, 42, 0.08);
  }
  ${props => props.selected && `
    border-color: ${primaryColor(props.theme)};
    background-color: ${tintBg(props.theme)};
  `}
`;

export const TemplateCardTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 4px;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: break-word;
`;

export const TemplateCardDescription = styled.div`
  font-size: 12px;
  color: #4B5563;
  margin-bottom: 8px;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: break-word;
`;

export const TemplateCardSteps = styled.div`
  font-size: 11px;
  color: #9CA3AF;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

export const CostSavedHelper = styled.span`
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: #6B7280;
  font-family: "Petasense Open Sans", sans-serif;
  font-weight: 400;
`;

export const EditLink = styled.span`
  color: ${props => primaryColor(props.theme)};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  font-family: "Petasense Open Sans", sans-serif;
  &:hover {
    text-decoration: underline;
  }
`;

export const DescriptionBoxWrapper = styled.div`
  cursor: ${props => props.isEditing ? 'auto' : 'pointer'};
  textarea {
    background-color: white !important;
    border: 1px solid #E4E7EC !important;
    border-radius: 6px;
    padding: 12px 16px;
    font-size: 13px;
    color: #1D2939;
    font-family: "Petasense Open Sans", sans-serif;
    font-weight: 400;
    line-height: 1.625;
    margin-bottom: 0 !important;
    transition: border-color 0.2s ease;
    cursor: ${props => props.isEditing ? 'text' : 'pointer'};
    &:hover {
      border-color: ${props => primaryColor(props.theme)} !important;
    }
    &:focus {
      border-color: ${props => primaryColor(props.theme)} !important;
      outline: none;
      box-shadow: none;
    }
  }
  > div {
    margin-bottom: 0 !important;
  }
  label {
    display: none !important;
  }
`;

export const RecommendedActionBoxWrapper = styled.div`
  cursor: ${props => props.isEditing ? 'auto' : 'pointer'};
  textarea {
    background-color: white !important;
    border: 1px solid #E4E7EC !important;
    border-radius: 6px;
    padding: 12px 16px;
    font-size: 13px;
    color: #1D2939;
    font-family: "Petasense Open Sans", sans-serif;
    font-weight: 400;
    line-height: 1.625;
    margin-bottom: 0 !important;
    transition: border-color 0.2s ease;
    cursor: ${props => props.isEditing ? 'text' : 'pointer'};
    &:hover {
      border-color: ${props => primaryColor(props.theme)} !important;
    }
    &:focus {
      border-color: ${props => primaryColor(props.theme)} !important;
      outline: none;
      box-shadow: none;
    }
  }
  > div {
    margin-bottom: 0 !important;
  }
  label {
    display: none !important;
  }
`;

export const TaskTitleWrapper = styled.div`
  width: 100%;
  ${FlexContainer} {
    width: 100%;
  }
  > div {
    margin-bottom: 0 !important;
  }
  h2.title {
    font-family: 'Petasense Open Sans', sans-serif;
    font-size: 18px;
    font-weight: 600;
    color: #1D2939;
    margin: 0;
    line-height: 1.4;
  }
  input {
    font-family: 'Petasense Open Sans', sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: #344054;
    background-color: white !important;
    border: 1px solid #E4E7EC !important;
    border-radius: 6px;
    padding: 8px 16px;
    line-height: 1.4;
    width: 100%;
    max-width: 100%;
    transition: border-color 0.2s ease;
    &:hover {
      border-color: ${props => primaryColor(props.theme)} !important;
    }
    &:focus {
      border-color: ${props => primaryColor(props.theme)} !important;
      outline: none;
      box-shadow: none;
    }
  }
`;

/* Clickable template title - theme color hover to indicate it opens template dropdown */
export const TemplateTitleClickable = styled.h2`
  font-family: 'Petasense Open Sans', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #1D2939;
  margin: 0;
  line-height: 1.4;
  cursor: pointer;
  transition: color 0.2s ease;
  ${props => !props.dropdownOpen && `
    &:hover {
      color: ${primaryColor(props.theme)};
    }
  `}
`;

export const TaskTitleUnderline = styled.div`
  border-bottom: 1px solid #E4E7EC;
  width: 100%;
  margin-top: 2px;
`;

export const CostSavedInputWrapper = styled.div`
  margin-bottom: ${props => (props.hasFooter ? '0' : '0')};
  input {
    background-color: white !important;
    border: 1px solid #E4E7EC !important;
    border-radius: 6px;
    padding: 6px 10px;
    font-size: 13px;
    color: #1D2939;
    font-family: "Petasense Open Sans", sans-serif;
    font-weight: 400;
    width: 100%;
    transition: border-color 0.2s ease;
    cursor: ${props => (props.hasFooter ? 'text' : 'pointer')};
    &:focus {
      outline: none;
      border-color: ${props => primaryColor(props.theme)} !important;
      box-shadow: none;
    }
  }
`;

export const CostSavedOuterWrapper = styled.div`
  background-color: ${props => getCostSavedHighlightStyles(props.theme).backgroundColor};
  border: 1px solid ${props => getCostSavedHighlightStyles(props.theme).borderColor};
  border-radius: 8px;
  padding: 12px 16px;
  cursor: ${props => (props.isEditing ? 'auto' : 'pointer')};
  transition: box-shadow 0.2s ease;
  &:hover {
    ${props => !props.isEditing && `box-shadow: 0 0 0 1px ${getCostSavedHighlightStyles(props.theme).boxShadow};`}
  }
`;

export const CloseTaskInputWrapper = styled.div`
  margin-bottom: 1em;
  input {
    border: 1px solid #E4E7EC;
    border-radius: 6px;
    padding: 6px 10px;
    font-size: 13px;
    color: #1D2939;
    transition: border-color 0.2s ease;
    &:focus {
      outline: none;
      border-color: ${props => primaryColor(props.theme)};
      box-shadow: none;
    }
  }
`;

export const fontSettings = `
  font-family: "Petasense Open Sans";
  font-size: 16px;
  font-weight: 400;
`;

